import { Activity, LogOut, PauseOctagon, Wallet2 } from 'lucide-react'
import { NavLink, Outlet } from 'react-router-dom'
import { useReadOnlyState } from '@/hooks/useOps'
import { cn } from '@/lib/cn'
import { shortAddress } from '@/lib/format'
import { useSession } from '@/lib/session'
import { SignIn } from '@/pages/SignIn'
import { Badge, Skeleton } from '@/ui/primitives'

const NAV = [
  { to: '/', label: 'Ops board', icon: Activity, end: true },
  { to: '/withdrawals', label: 'Withdrawals', icon: Wallet2, end: false },
  { to: '/kill-switch', label: 'Kill switch', icon: PauseOctagon, end: false },
] as const

/**
 * The shell, and the gate.
 *
 * Authorisation is enforced HERE rather than page by page. Every route in this app is
 * an admin route, so putting the check in the shell means a page added later cannot
 * forget it — the failure mode of per-page guards is silent, and the thing it exposes
 * is the payout queue.
 *
 * This is a rendering gate, not a security boundary. The API re-checks admin rights on
 * every call, so bypassing this in devtools buys a layout full of 403s.
 */
export function AdminShell() {
  const { restoring, isSignedIn, isAdmin, session, signOut } = useSession()
  const readOnly = useReadOnlyState()

  if (restoring) return <Booting />
  if (!isSignedIn || !isAdmin) return <SignIn />

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[224px_1fr]">
      {/* Rail. Static on mobile so it scrolls away rather than eating a phone screen. */}
      <aside className="surface-glass border-b border-line lg:sticky lg:top-0 lg:h-dvh lg:border-r lg:border-b-0">
        <div className="flex items-center gap-2.5 px-5 py-4">
          <img src="/brand/mark.svg" alt="" className="size-7" />
          <div className="min-w-0">
            <p className="display text-sm leading-tight">CrystalDEX</p>
            <p className="label">Operations</p>
          </div>
        </div>

        <nav className="flex gap-1 px-3 pb-3 lg:flex-col">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-[10px] px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-accent-wash font-medium text-accent'
                    : 'text-muted hover:bg-accent-wash hover:text-ink',
                )
              }
            >
              <Icon className="size-4 shrink-0" aria-hidden />
              {label}
            </NavLink>
          ))}
        </nav>

        <p className="hidden px-5 pb-5 text-[10px] leading-relaxed text-faint lg:block">
          API
          <br />
          <span className="font-mono break-all">
            {import.meta.env.VITE_API_URL ?? 'http://localhost:3101'}
          </span>
        </p>
      </aside>

      <div className="min-w-0">
        <header className="surface-glass sticky top-0 z-20 flex items-center justify-between gap-4 border-b border-line px-6 py-3 backdrop-blur">
          {/*
           * The switch state lives in the chrome, on every page, not only on the page
           * that flips it. An operator reading the ops board needs to know the product
           * is halted without navigating to find out.
           */}
          {readOnly.isLoading ? (
            <Skeleton className="h-6 w-24" />
          ) : readOnly.data ? (
            <Badge tone="danger">Read-only</Badge>
          ) : (
            <Badge tone="positive">Live</Badge>
          )}

          <div className="flex items-center gap-3">
            <span className="tnum hidden text-xs text-muted sm:inline">
              {session ? shortAddress(session.address) : '—'}
            </span>
            <button
              onClick={() => void signOut()}
              className="flex items-center gap-1.5 rounded-[10px] px-2.5 py-1.5 text-xs text-muted transition-colors hover:bg-accent-wash hover:text-ink"
            >
              <LogOut className="size-3.5" aria-hidden />
              Sign out
            </button>
          </div>
        </header>

        {readOnly.data && (
          <div className="border-b border-fault-edge bg-fault/10 px-6 py-2.5 text-xs text-fault-text">
            <span className="font-mono font-medium tracking-[0.1em] uppercase">Halted</span> — the
            kill switch is on. Every write-implying endpoint is refusing. Existing on-chain
            positions are untouched.
          </div>
        )}

        <main className="page-enter px-6 py-7">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

/**
 * The silent-refresh window.
 *
 * Deliberately NOT the sign-in screen: the cookie may be about to restore a session,
 * and flashing "connect a wallet" for 200ms before replacing it with a dashboard reads
 * as a bug and tempts the operator into clicking something they did not need to.
 */
function Booting() {
  return (
    <div className="grid min-h-dvh place-items-center">
      <div className="flex items-center gap-3 text-muted">
        <img src="/brand/mark.svg" alt="" className="size-6 opacity-60" />
        <span className="label">Restoring session</span>
      </div>
    </div>
  )
}
